import React, { useState, useEffect } from "react";
import { useApp } from "context";
import { Grid } from "@mui/material";
import { Custom } from "components";
import { Constants, Enums, Helpers, Store } from "utils";
import { PairValue } from "types";
import { IAppointment, IEvent } from "interfaces";

type Props = {
  selectedEvent: IEvent | null;
  providers: PairValue[];
  opened: boolean;
  onClose: () => void;
  selectedHealth360?: IAppointment | null;
  handleUpsertEvent: (
    event: IEvent,
    ids?: { clinicId?: number; providerId?: number }
  ) => void;
  handleDeleteEvent: (event: IEvent) => void;
};

export const BigCalendar = (props: Props) => {
  const { t, theme, user, service, locale } = useApp();

  const [tempSelectedEvent, setTempSelectedEvent] = useState<IEvent | null>(
    props.selectedEvent
  );
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(
    null
  );

  useEffect(() => {
    setTempSelectedEvent(props.opened ? props.selectedEvent : null);
  }, [props.opened]);

  useEffect(() => {
    if (
      tempSelectedEvent?.title !== Enums.EnumEventTitle.ProviderAvailability
    ) {
      setSelectedProviderId(null);
    }
  }, [props.opened, tempSelectedEvent]);

  const isClinic = Store.sessionStorage.isClinic();

  const handleEventTitleChange = (event: any) => {
    // const fieldName = event.target.name;
    const newValue = event.target.value;

    setTempSelectedEvent({
      ...(tempSelectedEvent as IEvent),
      title: Constants.bigCalendar.clinicItems.find((t) => t.key === newValue)
        ?.value,
    });
  };

  const handleProviderAvailabilityChange = (event: any) => {
    // const fieldName = event.target.name;
    const newValue = event.target.value;

    setTempSelectedEvent({
      ...(tempSelectedEvent as IEvent),
      isPrimary: !newValue,
    });
  };

  const handleEventNotesChange = (event: any) => {
    // const fieldName = event.target.name;
    const newValue = event.target.value;

    setTempSelectedEvent({ ...(tempSelectedEvent as IEvent), notes: newValue });
  };

  const toggleConfirmaion = () =>
    isClinic &&
    setTempSelectedEvent({
      ...(tempSelectedEvent as IEvent),
      isConfirmed: !tempSelectedEvent?.isConfirmed,
    });

  const descriptionComponent = (
    provider: string,
    patient: string,
    scheduledTime: string,
    checkInTime: string
  ) => {
    return (
      <>
        <div style={{ fontSize: "0.75rem" }}>Provider:</div>
        <div style={{ marginBottom: "0.5rem" }}>{provider}</div>
        <div style={{ fontSize: "0.75rem" }}>Patient:</div>
        <div style={{ marginBottom: "0.5rem" }}>
          {Helpers.replaceComma(patient)}
        </div>
        <div style={{ fontSize: "0.75rem" }}>
          {checkInTime ? "Check-in" : "Scheduled"} Time:
        </div>
        <div>
          {new Date(
            checkInTime ? checkInTime : scheduledTime
          ).toLocaleTimeString(locale, Constants.DATE_FORMAT)}
        </div>
      </>
    );
  };

  const controlComponent = () => {
    if (
      tempSelectedEvent?.title &&
      [
        Enums.EnumEventTitle.Health360InClinic,
        Enums.EnumEventTitle.Health360Virtual,
      ].includes(tempSelectedEvent.title)
    ) {
      return descriptionComponent(
        tempSelectedEvent?.resource.provider ?? "",
        tempSelectedEvent?.resource.patient ?? "",
        tempSelectedEvent?.resource.scheduledTime ?? "",
        tempSelectedEvent?.resource.checkInTime ?? ""
      );
    }

    if (isClinic) {
      if (tempSelectedEvent?.providerId === null) {
        if (tempSelectedEvent.eId === -1 && props.selectedHealth360) {
          return descriptionComponent(
            props.selectedHealth360.providerName ?? "",
            props.selectedHealth360.username,
            props.selectedHealth360.scheduledTime,
            props.selectedHealth360.checkInTime
          );
        } else {
          return (
            <Custom.Select
              label={"Event Type"}
              name={"title"}
              items={Constants.bigCalendar.clinicItems}
              onDropdownChange={handleEventTitleChange}
              value={
                Constants.bigCalendar.clinicItems.find(
                  (t) => t.value === tempSelectedEvent?.title
                )?.key
              }
            />
          );
        }
      } else {
        return (
          <>
            <div>{tempSelectedEvent?.owner}</div>
            <div>
              {tempSelectedEvent?.isPrimary
                ? " Primary Availability"
                : " Secondary Availability"}
            </div>
          </>
        );
      }
    } else {
      return (
        <Custom.Select
          label={"Availability"}
          name={"isPrimary"}
          items={Constants.bigCalendar.providerItems}
          onDropdownChange={handleProviderAvailabilityChange}
          value={tempSelectedEvent?.isPrimary ? 0 : 1}
        />
      );
    }
  };

  return (
    <Custom.Modal
      header={tempSelectedEvent?.title ?? "Other"}
      sx={{ maxWidth: 500 }}
      opened={props.opened}
      onClose={props.onClose}
    >
      <Grid container spacing={theme.margin.sm}>
        <Grid item xs={12} md={6}>
          <Custom.TextField
            label={"Start"}
            readOnly
            value={new Date(tempSelectedEvent?.start ?? "").toLocaleTimeString(
              locale,
              Constants.DATE_FORMAT
            )}
          ></Custom.TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <Custom.TextField
            label={"End"}
            readOnly
            value={new Date(tempSelectedEvent?.end ?? "").toLocaleTimeString(
              locale,
              Constants.DATE_FORMAT
            )}
          ></Custom.TextField>
        </Grid>
        <Grid item xs={12} md={12} sx={{ marginTop: "3rem" }}></Grid>
        <Grid item xs={6} md={6}>
          {controlComponent()}
        </Grid>
        {isClinic &&
          tempSelectedEvent?.title ===
            Enums.EnumEventTitle.ProviderAvailability &&
          tempSelectedEvent.eId === -1 && (
            <Grid item xs={6} md={6}>
              <Custom.Select
                label={"Provider"}
                name={"provider"}
                items={props.providers}
                onDropdownChange={(e) =>
                  setSelectedProviderId(e.target.value as number)
                }
              />
            </Grid>
          )}
        {tempSelectedEvent?.providerId !== null && (
          <Grid item xs={6} md={6}>
            <Custom.Chip
              label={tempSelectedEvent?.isConfirmed ? "Confirmed" : "Pending"}
              sx={{
                width: 100,
                backgroundColor: tempSelectedEvent?.isConfirmed
                  ? theme.color.status.success.color
                  : theme.color.primary.color,
                "&:hover": {
                  backgroundColor: tempSelectedEvent?.isConfirmed
                    ? theme.color.status.success.color
                    : theme.color.primary.color,
                },
              }}
              onClick={toggleConfirmaion}
            ></Custom.Chip>
          </Grid>
        )}
        <Grid item xs={12} md={12}>
          <Custom.TextField
            multiline={true}
            label={"Notes"}
            name={"notes"}
            value={tempSelectedEvent?.notes ?? ""}
            onChange={handleEventNotesChange}
          ></Custom.TextField>
        </Grid>
      </Grid>
      <div style={{ marginTop: "0.75rem" }}>
        <Custom.Button
          size="large"
          disabled={!isClinic && !!tempSelectedEvent?.isConfirmed}
          onClick={() =>
            props.handleUpsertEvent(
              {
                ...tempSelectedEvent,
                ...(tempSelectedEvent?.eId === -1 &&
                  props.selectedHealth360 && {
                    title: Enums.EnumEventTitle.Health360Virtual,
                    appointmentId: props.selectedHealth360?.appointmentId,
                  }),
                ...(selectedProviderId && {
                  isPrimary: false,
                  isConfirmed: false,
                }),
              } as IEvent,
              { ...(selectedProviderId && { providerId: selectedProviderId }) }
            )
          }
        >
          {t.action.submit}
        </Custom.Button>
        <Custom.Button
          sx={{ float: "right" }}
          size="large"
          disabled={
            tempSelectedEvent?.eId === -1 ||
            (!isClinic && !!tempSelectedEvent?.isConfirmed)
          }
          onClick={() => props.handleDeleteEvent(tempSelectedEvent as IEvent)}
        >
          {t.action.delete}
        </Custom.Button>
      </div>
    </Custom.Modal>
  );
};
